import { useEffect } from 'react';
import { useCanvasStore } from './canvas-store';
import { mergePlaybookJobs, pollPlaybookJobsOnce } from './playbook-queue';

export function usePlaybookQueue(projectId: number | null, loading: boolean) {
  useEffect(() => {
    if (projectId == null || loading) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cursors = new Map<number, number>();
    const settledJobs = new Set<number>();
    const current = () => !disposed && useCanvasStore.getState().activeProjectId === projectId;
    const poll = async () => {
      try {
        await pollPlaybookJobsOnce({ projectId, cursors, settledJobs, isCurrent: current,
          getMessages: () => useCanvasStore.getState().projects[projectId]?.messages ?? [],
          writeMessages: update => { if (current()) useCanvasStore.getState()._writeMessages(projectId, update); },
          reconcile: () => useCanvasStore.getState().reconcileImagesFromServer(projectId),
        });
      } catch { /* Retain the saved card and retry transient disconnects. */ }
      if (current()) timer = setTimeout(poll, 3000);
    };
    void poll();
    return () => { disposed = true; if (timer) clearTimeout(timer); };
  }, [projectId, loading]);
}

export async function cancelPlaybookJob(projectId: number, jobId: number): Promise<void> {
  const response = await fetch(`/api/playbook-jobs/${jobId}/cancel`, { method: 'POST' });
  if (!response.ok) throw new Error('Could not cancel this playbook. Please try again.');
  const payload = await response.json();
  if (payload.job?.projectId !== projectId || payload.job?.id !== jobId) throw new Error('Cancellation response did not match this task.');
  useCanvasStore.getState()._writeMessages(projectId, messages => mergePlaybookJobs(messages, [payload.job], projectId));
}
